import { useState, useEffect } from 'react'
import { Loader2, MapPin, Search, Check, ChevronRight, Edit3 } from 'lucide-react'
import { lookupAddresses, getAddressDetails, lookupPostcode, generateGoogleMapsUrl, type AddressResult } from '@/lib/postcode'

export interface AddressData {
  name: string
  address: string
  city: string
  postcode: string
  country: string
  googleMapsUrl: string
}

interface AddressLookupProps {
  value: AddressData
  onChange: (address: AddressData) => void
  buildingNameLabel?: string
  buildingNamePlaceholder?: string
}

type LookupState = 'idle' | 'loading' | 'results' | 'manual'

export function AddressLookup({ 
  value, 
  onChange,
  buildingNameLabel = 'Building Name',
  buildingNamePlaceholder = 'e.g. The Hub, Unit 5, Sunrise Office'
}: AddressLookupProps) {
  const [postcodeInput, setPostcodeInput] = useState(value.postcode || '')
  const [state, setState] = useState<LookupState>(value.city ? 'manual' : 'idle')
  const [addresses, setAddresses] = useState<AddressResult[]>([])
  const [filteredAddresses, setFilteredAddresses] = useState<AddressResult[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [selectingAddress, setSelectingAddress] = useState(false)

  // Filter addresses based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredAddresses(addresses)
    } else {
      const query = searchQuery.toLowerCase()
      setFilteredAddresses(
        addresses.filter(addr => 
          addr.formatted.toLowerCase().includes(query) ||
          addr.buildingName.toLowerCase().includes(query) ||
          addr.buildingNumber.toLowerCase().includes(query) ||
          addr.line1.toLowerCase().includes(query)
        )
      )
    }
  }, [searchQuery, addresses])

  const handleFindAddress = async () => {
    if (!postcodeInput.trim()) {
      setError('Please enter a postcode')
      return
    }

    setError(null)
    setState('loading')

    try {
      const results = await lookupAddresses(postcodeInput)
      setAddresses(results)
      setFilteredAddresses(results)
      setState('results')
      
      // Update postcode to formatted version
      if (results.length > 0) {
        setPostcodeInput(results[0].postcode)
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to find addresses'
      
      if (errorMsg.includes('manually')) {
        await handleManualEntry()
      } else {
        setError(errorMsg)
        setState('idle')
      }
    }
  }

  const handleSelectAddress = async (address: AddressResult) => {
    setSelectingAddress(true)
    
    try {
      // Fetch full address details from the API
      const fullAddress = address.id ? await getAddressDetails(address.id) : null
      const addr = fullAddress || address
      
      // Build the street address from available parts
      const streetAddress = [addr.buildingNumber, addr.thoroughfare || addr.line1]
        .filter(Boolean)
        .join(' ')

      // Build the building name
      let buildingName = addr.buildingName || ''
      if (!buildingName && addr.line1 && addr.line1 !== addr.thoroughfare) {
        buildingName = addr.line1
      }

      const googleMapsUrl = addr.latitude && addr.longitude
        ? generateGoogleMapsUrl(addr.latitude, addr.longitude)
        : ''

      onChange({
        name: buildingName,
        address: streetAddress || addr.line1,
        city: addr.city,
        postcode: addr.postcode,
        country: addr.country,
        googleMapsUrl,
      })

      setState('manual')
      setSearchQuery('')
    } finally {
      setSelectingAddress(false)
    }
  }

  const handleManualEntry = async () => {
    setState('manual')
    
    // Still try to get coordinates for Google Maps URL
    try {
      const postcodeData = await lookupPostcode(postcodeInput)
      onChange({
        ...value,
        postcode: postcodeData.postcode,
        city: postcodeData.city,
        country: postcodeData.country,
        googleMapsUrl: generateGoogleMapsUrl(postcodeData.latitude, postcodeData.longitude),
      })
      setPostcodeInput(postcodeData.postcode)
    } catch {
      // Keep existing values
    }
  }

  const handleReset = () => {
    setState('idle')
    setAddresses([])
    setFilteredAddresses([])
    setSearchQuery('')
    setError(null)
    onChange({
      name: '',
      address: '',
      city: '',
      postcode: postcodeInput,
      country: '',
      googleMapsUrl: '',
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (state === 'idle') {
        handleFindAddress()
      }
    }
  }

  const updateField = (field: keyof AddressData, fieldValue: string) => {
    onChange({ ...value, [field]: fieldValue })
  }

  // ========== STEP 1: Enter Postcode ==========
  if (state === 'idle' || state === 'loading') {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="postcode" className="block text-base font-medium text-gray-700">
            Enter your postcode
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                id="postcode"
                type="text"
                value={postcodeInput}
                onChange={(e) => setPostcodeInput(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                placeholder="e.g. SW1A 1AA"
                className="w-full pl-11 pr-4 py-4 text-xl border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={state === 'loading'}
                autoFocus
              />
            </div>
            <button
              type="button"
              onClick={handleFindAddress}
              disabled={state === 'loading' || !postcodeInput.trim()}
              className="px-6 py-4 bg-primary-600 text-white text-lg font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
            >
              {state === 'loading' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
              Find
            </button>
          </div>
          {error && (
            <p className="text-sm text-red-600 mt-2">{error}</p>
          )}
        </div>
      </div>
    )
  }

  // ========== STEP 2: Select Address (simple list) ==========
  if (state === 'results') {
    return (
      <div className="space-y-4">
        {/* Header with postcode and change option */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
              <MapPin className="w-4 h-4 text-primary-600" />
            </div>
            <span className="font-medium text-gray-900">{postcodeInput}</span>
          </div>
          <button
            type="button"
            onClick={handleReset}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Change postcode
          </button>
        </div>

        {/* Search filter - only show if many addresses */}
        {addresses.length > 5 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Type to filter addresses..."
              className="w-full pl-10 pr-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        )}

        {/* Address list - simple, large tap targets */}
        <div className="space-y-2">
          <p className="text-sm text-gray-600 font-medium">
            Tap your address ({filteredAddresses.length} found)
          </p>
          
          <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
            {filteredAddresses.map((address, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSelectAddress(address)}
                disabled={selectingAddress}
                className="w-full px-4 py-4 text-left hover:bg-primary-50 focus:bg-primary-50 focus:outline-none transition-colors flex items-center justify-between gap-3 disabled:opacity-50"
              >
                <span className="text-base text-gray-800">{address.formatted}</span>
                <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
              </button>
            ))}
          </div>

          {filteredAddresses.length === 0 && (
            <div className="text-center py-4 text-gray-500">
              No addresses match your search
            </div>
          )}
        </div>

        {/* Manual entry option */}
        <button
          type="button"
          onClick={handleManualEntry}
          className="w-full py-3 text-primary-600 hover:text-primary-700 font-medium text-center border-2 border-dashed border-gray-300 rounded-xl hover:border-primary-300 hover:bg-primary-50/50 transition-colors"
        >
          Can't find your address? Enter it manually
        </button>
      </div>
    )
  }

  // ========== STEP 3: Edit/Confirm Details ==========
  if (state === 'manual') {
    return (
      <div className="space-y-4">
        {/* Success banner */}
        {value.city && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-green-800">{value.city}, {value.postcode}</p>
              {value.address && (
                <p className="text-sm text-green-700 truncate">{value.address}</p>
              )}
            </div>
            <button
              type="button"
              onClick={handleReset}
              className="p-2 text-green-700 hover:bg-green-100 rounded-lg transition-colors shrink-0"
              title="Change address"
            >
              <Edit3 className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Building name - main field */}
        <div className="space-y-2">
          <label htmlFor="buildingName" className="block text-base font-medium text-gray-700">
            {buildingNameLabel} <span className="text-red-500">*</span>
          </label>
          <input
            id="buildingName"
            type="text"
            value={value.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder={buildingNamePlaceholder}
            className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            autoFocus
          />
          <p className="text-sm text-gray-500">
            Give your building a name people will recognise
          </p>
        </div>

        {/* Collapsible address details */}
        <details className="group">
          <summary className="flex items-center justify-between cursor-pointer py-2 text-sm text-gray-600 hover:text-gray-900">
            <span className="font-medium">Edit address details</span>
            <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
          </summary>
          
          <div className="mt-3 space-y-3 pl-0">
            <div className="space-y-1">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                Street Address
              </label>
              <input
                id="address"
                type="text"
                value={value.address}
                onChange={(e) => updateField('address', e.target.value)}
                placeholder="e.g. 123 High Street"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">City/Town</label>
                <input
                  id="city"
                  type="text"
                  value={value.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="country" className="block text-sm font-medium text-gray-700">Country</label>
                <input
                  id="country"
                  type="text"
                  value={value.country}
                  onChange={(e) => updateField('country', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>
        </details>
      </div>
    )
  }

  return null
}
